import React, { useState, useEffect, useRef } from "react";
import icons from './data/icons';
import { getExperiences } from "./api/experienceService";
export const ExperienceTitle = "Professional Experience";
export const Current = "Current";
export const Total = "Total:"
export const getSkillIcon = (skill) => {
  const iconName =
    typeof skill === "string"
      ? skill
      : skill?.icon || "";

  const iconColor =
    typeof skill === "object"
      ? skill?.color || "#106ae8"
      : "#106ae8";

  if (!iconName) {
    return <icons.Laptop color={iconColor} />;
  }

  if (icons[iconName]) {
    const IconComponent = icons[iconName];

    return <IconComponent size={20} color={iconColor} />;
  }

  const normalizedName = iconName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

  const matchedKey = Object.keys(icons).find((key) =>
    key
      .toLowerCase()
      .replace(/^(fa|si|md|hi|ri|io|lu)/, "")
      .replace(/[^a-z0-9]/g, "") === normalizedName
  );

  if (matchedKey) {
    const IconComponent = icons[matchedKey];

    return <IconComponent size={20} color={iconColor} />;
  }

  return <icons.Laptop color={iconColor} />;
};
const parseDate = (str) => {
  if (!str || typeof str !== "string") {
    return new Date(0); // default date
  }

  if (str.toLowerCase().includes("present")) {
    return new Date();
  }

  const [monthStr, yearStr] = str.trim().split(" ");

  if (!monthStr || !yearStr) {
    return new Date(0);
  }

  return new Date(`${monthStr} 1, ${yearStr}`);
};

const calculateMonths = (start, end) => {
  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
  return months <= 0 ? 0 : months;
};

const formatDuration = (months) => {
  if (months < 1) return "0 mos";
  const years = Math.floor(months / 12);
  const rem = months % 12;
  let result = "";
  if (years === 1) result += "1 yr";
  else if (years > 1) result += `${years} yrs`;
  if (rem === 1) result += (result ? " " : "") + "1 mo";
  else if (rem > 1) result += (result ? " " : "") + `${rem} mos`;
  return result.trim();
};

const getWorkingExperience = (positions) => {
  if (!positions || positions.length === 0) return "0 mos";

  const ranges = positions.map(pos => {
    const [startStr, endStr] = pos.duration.split("-").map(s => s.trim());
    return {
      start: parseDate(startStr),
      end: parseDate(endStr),
    };
  });

  ranges.sort((a, b) => a.start - b.start);

  let totalMonths = 0;
  let currentStart = ranges[0].start;
  let currentEnd = ranges[0].end;

  for (let i = 1; i < ranges.length; i++) {
    const { start, end } = ranges[i];

    if (start <= currentEnd) {
      if (end > currentEnd) currentEnd = end;
    } else {
      totalMonths += calculateMonths(currentStart, currentEnd);
      currentStart = start;
      currentEnd = end;
    }
  }

  totalMonths += calculateMonths(currentStart, currentEnd);

  return formatDuration(totalMonths);
};

const getPositionDurationSuffix = (duration) => {
  const [startStr, endStr] = duration.split("-").map(s => s.trim());
  const start = parseDate(startStr);
  const end = parseDate(endStr);
  const months = calculateMonths(start, end);
  return ` • ${formatDuration(months)}`;
};

const DESCRIPTION_PREVIEW_COUNT = 1;
const SKILLS_PREVIEW_COUNT = 3;
const EXPERIENCE_LIMIT = 4;

const ExperienceTimeline = () => {
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [visibleItems, setVisibleItems] = useState(new Set());
  const [showAllExperience, setShowAllExperience] = useState(false);
  const [experienceData, setExperienceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const itemRefs = useRef([]);
  const publicExperienceData = experienceData.filter(
    company =>
      company.positions?.some(
        position => position.countExperience !== false
      )
  );
  useEffect(() => {
    const fetchExperiences = async () => {
      try {
        const res = await getExperiences();
        setExperienceData(res.data.data);
      } catch (error) {
        console.error("Failed to fetch experiences:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchExperiences();
  }, []);
  const toggleDescription = (key) => {
    setExpandedDescriptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isCurrent = (duration) => {
    if (!duration) return false;
    return duration.toLowerCase().includes("present");
  };

  const toggleCompany = (companyId) => {
    setExpandedCompany(expandedCompany === companyId ? null : companyId);
  };
  const activeExperienceData = experienceData.filter(
    (company) => company?.isActive === true
  );
  const reversedExperience = [...activeExperienceData].reverse();

  const visibleExperience = showAllExperience
    ? reversedExperience
    : reversedExperience.slice(0, EXPERIENCE_LIMIT);

  useEffect(() => {
    setVisibleItems(new Set());
  }, [showAllExperience]);

  useEffect(() => {
    const observers = [];

    itemRefs.current.forEach((ref, index) => {
      if (!ref) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleItems(prev => {
              const next = new Set(prev);
              next.add(index);
              return next;
            });
          }
        },
        {
          threshold: 0.15,
        }
      );

      observer.observe(ref);
      observers.push(observer);
    });

    return () => observers.forEach(o => o.disconnect());
  }, [showAllExperience, visibleExperience.length]);

  const SkillsSection = ({ skills = [], useCardHover = false }) => {
    const [isHovered, setIsHovered] = useState(false);

    const remainingCount = Math.max(
      skills.length - SKILLS_PREVIEW_COUNT,
      0
    );

    const renderSkill = (skill, i) => {
      // Support old database format: "React"
      const iconName =
        typeof skill === "string"
          ? skill
          : skill?.icon;

      const label =
        typeof skill === "string"
          ? skill
          : skill?.label || skill?.icon || "";

      return (
        <span
          key={`${iconName}-${label}-${i}`}
          className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5
        bg-slate-800/50 text-slate-300 text-[10px] sm:text-xs
        rounded-lg border border-slate-700/50
        transition-all duration-300"
        >
          {getSkillIcon(skill)}
          <span>{label}</span>
        </span>
      );
    };

    return (
      <div
        className="mt-4"
        onMouseEnter={() => !useCardHover && setIsHovered(true)}
        onMouseLeave={() => !useCardHover && setIsHovered(false)}
      >
        <p className="text-sm font-semibold text-slate-400 mb-3">
          Skills:
        </p>

        <div className="flex flex-wrap gap-2">

          {/* FIRST 3 SKILLS */}
          {skills
            .slice(0, SKILLS_PREVIEW_COUNT)
            .map(renderSkill)}

          {/* +N */}
          {remainingCount > 0 && (
            <span
              className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5
            bg-slate-800/50 text-slate-300 text-[10px] sm:text-xs
            rounded-lg border border-slate-700/50
            transition-all duration-300
            ${useCardHover
                  ? "group-hover:hidden"
                  : isHovered
                    ? "hidden"
                    : ""
                }`}
            >
              +{remainingCount}
            </span>
          )}

          {/* REMAINING SKILLS */}
          <div
            className={
              useCardHover
                ? `opacity-0 max-h-0 overflow-hidden
                 group-hover:opacity-100 group-hover:max-h-40
                 transition-all duration-500 ease-out
                 flex flex-wrap gap-2`
                : isHovered
                  ? "flex flex-wrap gap-2"
                  : "hidden"
            }
          >
            {skills
              .slice(SKILLS_PREVIEW_COUNT)
              .map(renderSkill)}
          </div>

        </div>
      </div>
    );
  };

  return (
    <section
      id="experience"
      className="relative bg-[#0C121D] pt-16 sm:pt-20 pb-32 sm:pb-40 px-4 sm:px-6 lg:px-8 overflow-hidden isolate"
    >
      <div className="max-w-full mx-auto px-0 sm:px-0 lg:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
            <span className="text-white">
              {ExperienceTitle?.split(" ")[0]}
            </span>{" "}
            <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              {ExperienceTitle?.split(" ").slice(1).join(" ")}
            </span>
          </h2>
        </div>

        {/* Timeline Container */}
        <div className="relative">
          {visibleExperience.map((company, index) => {
            const positions = company.positions;
            const isSolo = positions.length === 1;
            const reversedPositions = [...positions].reverse();

            const currentPositions = reversedPositions.filter(pos =>
              isCurrent(pos.duration)
            );

            const shouldShowPreview =
              positions.length === 1 || currentPositions.length === 1;

            const previewPosition =
              currentPositions.length === 1
                ? currentPositions[0]
                : reversedPositions[0];

            const totalCompanyDuration = getWorkingExperience(positions);
            const allSameLocation = positions.every(p => p.location === positions[0].location);
            const sharedLocation = allSameLocation ? positions[0].location : null;
            const badgeStyles = [
              "bg-blue-600 shadow-blue-500/50",
              "bg-green-600 shadow-green-500/50",
              "bg-purple-600 shadow-purple-500/50",
              "bg-red-600 shadow-red-500/50",
              "bg-gradient-to-r from-cyan-600 to-blue-600 shadow-cyan-500/50",
              "bg-gradient-to-r from-purple-600 to-pink-600 shadow-pink-500/50",
              "bg-gradient-to-r from-emerald-600 to-teal-600 shadow-emerald-500/50",
            ];
            const bgVariants = [
              "bg-[linear-gradient(135deg,rgb(99,102,241),rgb(56,189,248))]",
              "bg-[linear-gradient(135deg,rgb(74,222,128),rgb(251,146,60))]",
              "bg-[linear-gradient(135deg,rgb(251,146,60),rgb(168,85,247))]",
              "bg-[linear-gradient(135deg,rgb(56,189,248),rgb(167,139,250))]",
              "bg-[linear-gradient(135deg,rgb(236,72,153),rgb(244,114,182))]",
              "bg-[linear-gradient(135deg,rgb(34,197,94),rgb(20,184,166))]",
              "bg-[linear-gradient(135deg,rgb(245,158,11),rgb(239,68,68))]",
              "bg-[linear-gradient(135deg,rgb(59,130,246),rgb(99,102,241))]",
              "bg-[linear-gradient(135deg,rgb(139,92,246),rgb(236,72,153))]",
              "bg-[linear-gradient(135deg,rgb(16,185,129),rgb(59,130,246))]",
              "bg-[linear-gradient(135deg,rgb(244,63,94),rgb(168,85,247))]",
              "bg-[linear-gradient(135deg,rgb(14,165,233),rgb(34,197,94))]",
            ];

            const lineGradients = [
              "bg-[linear-gradient(180deg,rgb(99,102,241,0.5),rgb(56,189,248,0.5))]",
              "bg-[linear-gradient(180deg,rgb(74,222,128,0.5),rgb(251,146,60,0.5))]",
              "bg-[linear-gradient(180deg,rgb(251,146,60,0.5),rgb(168,85,247,0.5))]",
              "bg-[linear-gradient(180deg,rgb(56,189,248,0.5),rgb(167,139,250,0.5))]",
              "bg-[linear-gradient(180deg,rgb(236,72,153,0.5),rgb(244,114,182,0.5))]",
              "bg-[linear-gradient(180deg,rgb(34,197,94,0.5),rgb(20,184,166,0.5))]",
              "bg-[linear-gradient(180deg,rgb(245,158,11,0.5),rgb(239,68,68,0.5))]",
              "bg-[linear-gradient(180deg,rgb(59,130,246,0.5),rgb(99,102,241,0.5))]",
            ];

            return (
              <div
                key={index}
                ref={el => (itemRefs.current[index] = el)}
                className={`relative mb-8 transition-all duration-700 ${visibleItems.has(index)
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
                  }`}
              >
                {/* Timeline Line */}
                <div
                  className={`hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-0.5
                    opacity-100
                    ${lineGradients[Math.floor(index / 1) % lineGradients.length]}`}
                  style={{
                    top: '3rem',
                    bottom: index === visibleExperience.length - 1 ? '0' : '-4rem'
                  }}
                />

                {/* Timeline Dot */}
                <div
                  className={`hidden lg:flex absolute left-1/2 transform -translate-x-1/2
    w-10 h-10 md:w-12 md:h-12 rounded-full items-center justify-center z-20
    transition-all duration-700 ease-out
    ${visibleItems.has(index)
                      ? 'scale-100 opacity-100 shadow-[0_0_25px_rgba(99,102,241,0.45)]'
                      : 'scale-0 opacity-0'}
    ${badgeStyles[index % badgeStyles.length]}`}
                >
                  {visibleItems.has(index) && (
                    <span className="absolute inset-0 rounded-full bg-white/25
      animate-[ping_2.5s_ease-in-out_infinite]"></span>
                  )}
                  <icons.Briefcase className="w-5 h-5 md:w-6 md:h-6 text-white/90 relative z-10" />
                </div>

                {/* Content Card */}
                <div
                  className={`
    lg:w-[calc(50%-3rem)]
    ${index % 2 === 0 ? 'lg:ml-0' : 'lg:ml-auto'}
  `}
                >
                  <div
                    className="
                      group
                      relative
                      bg-gradient-to-br from-slate-900/60 to-slate-800/60
                      backdrop-blur-xl
                      rounded-2xl
                      p-6
                      border border-slate-700/50
                      transition-all duration-500 ease-out
                      hover:-translate-y-2
                      hover:scale-[1.02]
                      hover:border-cyan-400/70
                      hover:shadow-[0_20px_40px_-12px_rgba(34,211,238,0.35)]
                    "
                  >
                    {/* Company Header */}
                    {company.company ? (
                      <div className="flex items-center gap-4 mb-6">
                        <div
                          className={`
                            w-12 h-12
                            sm:w-14 sm:h-14
                            md:w-16 md:h-16
                            rounded-lg sm:rounded-xl
                            overflow-hidden
                            flex items-center justify-center
                            flex-shrink-0
                            ${company.logo
                              ? "bg-white border border-slate-700"
                              : bgVariants[index % bgVariants.length]
                            }
                          `}
                        >
                          {company.logo ? (
                            <img
                              src={company.logo}
                              alt={company.company}
                              className="w-full h-full object-contain scale-127"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                e.currentTarget.parentElement.innerHTML = `
                                  <div class="w-full h-full flex items-center justify-center">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      class="w-8 h-8 text-white"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      stroke-width="2"
                                    >
                                      <path stroke-linecap="round" stroke-linejoin="round"
                                        d="M9 17v-6h6v6M3 10l9-7 9 7v10a1 1 0 01-1 1h-5v-6H9v6H4a1 1 0 01-1-1V10z" />
                                    </svg>
                                  </div>
                                `;
                              }}
                            />
                          ) : (
                            <icons.Briefcase className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
                          )}
                        </div>

                        <div className="flex-1 flex flex-col justify-center">
                          {company.link ? (
                            <a
                              href={company.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-lg sm:text-xl font-bold text-white mb-2 hover:text-cyan-400 transition-colors inline-flex items-center gap-2"
                            >
                              {company.company}
                            </a>
                          ) : (
                            <h3 className="text-lg sm:text-xl font-bold text-white mb-2">
                              {company.company}
                            </h3>
                          )}
                          <div className="flex flex-wrap gap-3 text-sm">
                            {sharedLocation?.trim() && (
                              <div
                                className="
      flex items-center gap-1.5
      text-slate-400
      text-xs sm:text-sm md:text-base
    "
                              >
                                <icons.MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                <span className="truncate sm:whitespace-normal">
                                  {sharedLocation}
                                </span>
                              </div>
                            )}

                            {!isSolo && (
                              <div className="flex items-center gap-1 text-slate-400 text-sm sm:text-base">
                                <icons.Calendar size={14} className="flex-shrink-0" />
                                <span>{Total} {totalCompanyDuration}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* No company name: logo left, title+badge top line, duration below */
                      <div className="flex items-center gap-4 mb-6">
                        <div
                          className={`
                            w-12 h-12
                            sm:w-14 sm:h-14
                            md:w-16 md:h-16
                            rounded-lg sm:rounded-xl
                            overflow-hidden
                            flex items-center justify-center
                            flex-shrink-0
                            ${company.logo
                              ? "bg-white border border-slate-700"
                              : bgVariants[index % bgVariants.length]
                            }
                          `}
                        >
                          {company.logo ? (
                            <img
                              src={company.logo}
                              alt={isSolo ? positions[0].title : previewPosition.title}
                              className="w-full h-full object-contain scale-110"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                e.currentTarget.parentElement.innerHTML = `
                                  <div class="w-full h-full flex items-center justify-center">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      class="w-8 h-8 text-white"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      stroke-width="2"
                                    >
                                      <path stroke-linecap="round" stroke-linejoin="round"
                                        d="M9 17v-6h6v6M3 10l9-7 9 7v10a1 1 0 01-1 1h-5v-6H9v6H4a1 1 0 01-1-1V10z" />
                                    </svg>
                                  </div>
                                `;
                              }}
                            />
                          ) : (
                            <icons.Briefcase className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
                          )}
                        </div>

                        <div className="flex-1 flex flex-col justify-center">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-lg sm:text-xl font-bold text-indigo-400">
                              {isSolo ? positions[0].title : previewPosition.title}
                            </h3>
                            {isCurrent(isSolo ? positions[0].duration : previewPosition.duration) && (
                              <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-green-500/20 text-green-300 text-[10px] sm:text-xs font-medium rounded-lg border border-green-500/30 animate-pulse">
                                {Current}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400 mt-1.5">
                            <icons.Calendar size={14} className="flex-shrink-0" />
                            <span>
                              {isSolo
                                ? `${positions[0].duration}${getPositionDurationSuffix(positions[0].duration)}`
                                : `${previewPosition.duration}${getPositionDurationSuffix(previewPosition.duration)}`}
                            </span>
                          </div>

                          {sharedLocation?.trim() && (
                            <div className="flex items-center gap-1.5 text-slate-400 text-xs sm:text-sm mt-1.5">
                              <icons.MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                              <span className="truncate sm:whitespace-normal">{sharedLocation}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {isSolo ? (
                      <div>
                        {/* Position Header (hidden when no company, since it's shown above already) */}
                        {company.company && (
                          <div className="flex flex-wrap items-center gap-2 mb-3">
                            <h4 className="text-base sm:text-lg font-semibold text-indigo-500">{positions[0].title}</h4>
                            {positions[0].type?.trim() && (
                              <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-blue-500/20 text-blue-300 text-[10px] sm:text-xs font-medium rounded-lg border border-blue-500/30">
                                {positions[0].type}
                              </span>
                            )}
                            {isCurrent(positions[0].duration) && (
                              <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-green-500/20 text-green-300 text-[10px] sm:text-xs font-medium rounded-lg border border-green-500/30 animate-pulse">
                                {Current}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Duration (hidden when no company, since it's shown above already) */}
                        {company.company && (
                          <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400 mb-4">
                            <icons.Calendar size={14} className="flex-shrink-0" />
                            <span>{positions[0].duration}{getPositionDurationSuffix(positions[0].duration)}</span>
                          </div>
                        )}

                        {/* Description */}
                        {positions[0].description && (
                          <ul className="space-y-2 mb-4">
                            {(expandedDescriptions[`solo-${index}`]
                              ? positions[0].description
                              : positions[0].description.slice(0, DESCRIPTION_PREVIEW_COUNT)
                            ).map((point, i, arr) => {
                              const isLast = i === arr.length - 1;
                              const canToggle = positions[0].description.length > DESCRIPTION_PREVIEW_COUNT;

                              return (
                                <li key={i} className="text-slate-300 text-xs sm:text-sm flex gap-2 sm:gap-3 items-baseline">
                                  <span className="text-cyan-400 text-lg sm:text-xl font-bold flex-shrink-0">•</span>
                                  <span className="flex-1">
                                    <span>{point}</span>
                                    {isLast && canToggle && (
                                      <button
                                        className="ml-1.5 sm:ml-2 text-slate-400 hover:text-slate-300 text-[10px] sm:text-sm transition-colors inline-block align-baseline"
                                        onClick={() => toggleDescription(`solo-${index}`)}
                                      >
                                        {expandedDescriptions[`solo-${index}`] ? "see less" : "....see more"}
                                      </button>
                                    )}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        {/* Location (only if no shared location AND this position has a real location) */}
                        {!sharedLocation && positions[0].location?.trim() && (
                          <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400 mb-4">
                            <icons.MapPin size={14} className="flex-shrink-0" />
                            <span>{positions[0].location}</span>
                          </div>
                        )}

                        {/* Skills */}
                        <SkillsSection skills={positions[0].skills} useCardHover={true} />
                      </div>
                    ) : (
                      <div>
                        {/* First Position Preview (hidden when no company, since it's shown above already) */}
                        {shouldShowPreview && company.company && (
                          <>
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                              <h4 className="text-lg font-semibold text-indigo-500">
                                {previewPosition.title}
                              </h4>

                              {previewPosition.type?.trim() && (
                                <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-blue-500/20 text-blue-300 text-[10px] sm:text-xs font-medium rounded-lg border border-blue-500/30">
                                  {previewPosition.type}
                                </span>
                              )}

                              {isCurrent(previewPosition.duration) && (
                                <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-green-500/20 text-green-300 text-[10px] sm:text-xs font-medium rounded-lg border border-green-500/30 animate-pulse">
                                  Current
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400 mb-4">
                              <icons.Calendar size={14} className="flex-shrink-0" />
                              <span>
                                {previewPosition.duration}
                                {getPositionDurationSuffix(previewPosition.duration)}
                              </span>
                            </div>
                          </>
                        )}

                        {/* Expand Button */}
                        <button
                          onClick={() => toggleCompany(index)}
                          className="
    inline-flex items-center justify-between gap-2
    px-2.5 py-1.5 sm:px-3 sm:py-1.5 md:px-4 md:py-2
    rounded-lg
    text-xs md:text-sm font-medium
    text-indigo-300
    bg-indigo-500/10
    border border-indigo-400/20
    transition-all duration-200 ease-out
    hover:bg-indigo-500/15
    hover:border-indigo-400/40
    hover:-translate-y-0.5
    active:translate-y-0
    active:scale-[0.98]
  "
                        >
                          <span className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap">
                            <span
                              className={`relative inline-flex h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full
        bg-cyan-400
        animate-[pulse_2s_ease-in-out_infinite]
        transition-opacity duration-300
        ${expandedCompany === index ? 'opacity-40' : 'opacity-100'}`}
                            ></span>

                            <span
                              className={`transition-all duration-300 ease-out text-xs sm:text-sm md:text-base
        ${expandedCompany === index
                                  ? 'opacity-100 translate-x-0'
                                  : 'opacity-90 translate-x-0'}
      `}
                            >
                              {expandedCompany === index ? 'Hide Positions' : 'Show Positions'}
                              <span className="opacity-70 ml-1 text-[10px] sm:text-xs md:text-sm">({positions.length})</span>
                            </span>
                          </span>

                          <icons.ChevronDown
                            size={14}
                            className={`flex-shrink-0
      transition-all duration-300 ease-out
      ${expandedCompany === index
                                ? 'rotate-180 scale-110 text-cyan-300'
                                : 'rotate-0 scale-100 text-indigo-300'}
    `}
                          />
                        </button>

                        {/* Expanded Positions */}
                        <div className={`overflow-hidden transition-all duration-500 ${expandedCompany === index ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                          <div className="space-y-6 pt-4 border-t border-slate-700/50">
                            {[...positions].reverse().map((position, posIdx) => (
                              <div key={posIdx} className="pl-4 border-l-2 border-cyan-500/30">
                                {/* Position Header */}
                                <div className="flex flex-wrap items-center gap-2 mb-3">
                                  <h4 className="text-base sm:text-lg font-semibold text-indigo-500">{position.title}</h4>
                                  {position.type?.trim() && (
                                    <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-blue-500/20 text-blue-300 text-[10px] sm:text-xs font-medium rounded-full border border-blue-500/30">
                                      {position.type}
                                    </span>
                                  )}
                                  {isCurrent(position.duration) && (
                                    <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-green-500/20 text-green-300 text-[10px] sm:text-xs font-medium rounded-lg border border-green-500/30 animate-pulse">
                                      Current
                                    </span>
                                  )}
                                </div>

                                {/* Duration */}
                                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400 mb-4">
                                  <icons.Calendar size={14} className="flex-shrink-0" />
                                  <span>{position.duration}{getPositionDurationSuffix(position.duration)}</span>
                                </div>

                                {/* Description */}
                                {position.description && position.description.length > 0 && (
                                  <ul className="space-y-2 mb-4">
                                    {(expandedDescriptions[`${index}-${posIdx}`]
                                      ? position.description
                                      : position.description.slice(0, DESCRIPTION_PREVIEW_COUNT)
                                    ).map((point, i, arr) => {
                                      const isLast = i === arr.length - 1;
                                      const canToggle = position.description.length > DESCRIPTION_PREVIEW_COUNT;

                                      return (
                                        <li key={i} className="text-slate-300 text-xs sm:text-sm flex gap-2 sm:gap-3 items-baseline">
                                          <span className="text-cyan-400 text-lg sm:text-xl font-bold flex-shrink-0">•</span>
                                          <span className="flex-1">
                                            {point}
                                            {isLast && canToggle && (
                                              <button
                                                className="ml-1.5 sm:ml-2 text-slate-400 hover:text-slate-200 text-[10px] sm:text-sm transition-colors inline-block align-baseline"
                                                onClick={() => toggleDescription(`${index}-${posIdx}`)}
                                              >
                                                {expandedDescriptions[`${index}-${posIdx}`] ? "see less" : "....see more"}
                                              </button>
                                            )}
                                          </span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}

                                {/* Location (only if no shared location AND this position has a real location) */}
                                {!sharedLocation && position.location?.trim() && (
                                  <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400 mb-4">
                                    <icons.MapPin size={14} className="flex-shrink-0" />
                                    <span>{position.location}</span>
                                  </div>
                                )}

                                {/* Skills */}
                                <SkillsSection skills={position.skills} useCardHover={false} />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* "See More" Button - MOVED OUTSIDE MAP */}
          {experienceData.length > EXPERIENCE_LIMIT && (
            <div className="flex justify-center mt-12">
              <button
                onClick={() => setShowAllExperience(prev => !prev)}
                className="
                  flex items-center gap-2
                  px-6 py-2
                  rounded-full
                  border border-cyan-400/40
                  text-cyan-300 text-sm font-semibold
                  bg-transparent
                  hover:bg-cyan-500/10
                  hover:border-cyan-300
                  hover:text-cyan-200
                  hover:scale-105
                  transition-all duration-300
                "
              >
                {showAllExperience ? (
                  <>
                    <icons.ChevronUp className="w-5 h-5" />
                    See Less Experience
                  </>
                ) : (
                  <>
                    <icons.ChevronDown className="w-5 h-5 animate-bounce" />
                    See {experienceData.length - EXPERIENCE_LIMIT} More Experience
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <div className="h-12 sm:h-16"></div>
      </div>
    </section>
  );
};

export default ExperienceTimeline;